import { useCallback, useEffect, useMemo, useState } from 'react'
import api from 'common/services/api'

import { TeacherInstance } from 'common/utils/types/account-types'
import { ClientTeacher } from 'common/utils/types/client-teacher'
import {
  ClientClassroomInstance,
  ClientStudentInstance,
  ClientTeacherInstance,
} from 'common/utils/types/account-types-client'
import { ClientClassroom } from 'common/utils/types/client-classroom'
import { ClientStudent } from 'common/utils/types/client-student'

import useGetCurrentUser from './useGetCurrentUser'

interface UseCurrentTeacherReturn {
  currentTeacher: ClientTeacherInstance | null
  classrooms: Record<string, any> | null
  students: ClientStudentInstance[] | null
  studentsPerClassroom: Record<string, ClientStudentInstance[]> | null
  updateTeacher: (prop: keyof TeacherInstance, value: any) => void
  refreshStudents: () => void
  ensureStudentsAndClassroom: (
    studentCodes: string[],
    classroomCode: string
  ) => Promise<void>
}

export default function useCurrentTeacher(): UseCurrentTeacherReturn {
  const { currentTeacher: teacherFromHook, fetchTeacher } = useGetCurrentUser()
  const [internalTeacherData, setInternalTeacherData] =
    useState<TeacherInstance | null>(null)

  useEffect(() => {
    setInternalTeacherData(teacherFromHook)
  }, [teacherFromHook])

  const clientTeacher = useMemo(() => {
    if (!internalTeacherData) return null
    return new ClientTeacher(internalTeacherData)
  }, [internalTeacherData])

  const classroomCodes = useMemo(() => {
    return clientTeacher?.classroomCodes(null) ?? null
  }, [clientTeacher])

  const [classrooms, setClassrooms] = useState<Record<string, any> | null>(null)
  const [students, setStudents] = useState<ClientStudentInstance[] | null>(null)
  const [studentCodes, setStudentCodes] = useState<Record<string, string>>({})
  const [studentsPerClassroom, setStudentsPerClassroom] = useState<Record<
    string,
    ClientStudentInstance[]
  > | null>(null)

  useEffect(() => {
    if (!classroomCodes) return
    api({
      url: '/api2/refresh',
      method: 'POST',
      data: {
        teachers: {},
        classrooms: Object.keys(classroomCodes).reduce((acc, code) => {
          acc[code] = { version: 0, secret: classroomCodes[code] }
          return acc
        }, {}),
        students: {},
      },
    })
      .then((response) => {
        setClassrooms(response.data.classrooms.items)
      })
      .catch((err) => console.error(err))
  }, [classroomCodes])

  useEffect(() => {
    if (!classrooms) return

    const allStudentCodes = {}
    Object.keys(classrooms).forEach((classroomCode) => {
      const classroomData = classrooms[classroomCode]
      if (classroomData) {
        const c: ClientClassroomInstance = new ClientClassroom(classroomData)
        Object.assign(allStudentCodes, c.studentCodes())
      }
    })
    // Update students state once after collecting all codes
    setStudentCodes(allStudentCodes)
  }, [classrooms])

  const refreshStudents = useCallback(() => {
    if (Object.keys(studentCodes).length === 0 || !classrooms) return

    api({
      url: '/api2/refresh',
      method: 'POST',
      data: {
        teachers: {},
        students: Object.keys(studentCodes).reduce((acc, code) => {
          acc[code] = { version: 0, secret: studentCodes[code] }
          return acc
        }, {}),
        classrooms: {},
      },
    })
      .then((response) => {
        setStudents(
          Object.keys(response.data.students.items).map(
            (s) => new ClientStudent(response.data.students.items[s])
          )
        )
        Object.values(classrooms).forEach((classroom) => {
          const c: ClientClassroomInstance = new ClientClassroom(classroom)
          const students = Object.keys(c.studentCodes() || {}).map(
            (s) => new ClientStudent(response.data.students.items[s])
          )
          setStudentsPerClassroom((prev) => ({
            ...prev,
            [classroom.code]: students,
          }))
        })
      })
      .catch((err) => console.error(err))
  }, [studentCodes, classrooms])

  useEffect(() => {
    if (Object.keys(studentCodes).length === 0) return
    refreshStudents()
  }, [studentCodes, refreshStudents])

  const processClassroomData = useCallback(
    (
      classroomData: any,
      classroomCode: string,
      missingStudentCodes: string[]
    ) => {
      const c: ClientClassroomInstance = new ClientClassroom(classroomData)
      const classroomStudentCodes = c.studentCodes() || {}
      setStudentCodes((prev) => ({
        ...(prev || {}),
        ...classroomStudentCodes,
      }))

      const studentsToRefresh = missingStudentCodes.filter(
        (code) => classroomStudentCodes[code]
      )

      return { classroomStudentCodes, studentsToRefresh }
    },
    []
  )

  const refreshAndUpdateStudents = useCallback(
    async (
      studentsToRefresh: string[],
      classroomStudentCodes: Record<string, string>,
      classroomCode: string
    ) => {
      if (studentsToRefresh.length === 0) {
        const classroomStudents = Object.keys(classroomStudentCodes)
          .map((code) => students?.find((s) => s.code === code))
          .filter((s): s is ClientStudentInstance => !!s)
        setStudentsPerClassroom((prev) => ({
          ...(prev || {}),
          [classroomCode]: classroomStudents,
        }))
        return
      }

      const studentsResponse = await api({
        url: '/api2/refresh',
        method: 'POST',
        data: {
          teachers: {},
          students: studentsToRefresh.reduce((acc, code) => {
            acc[code] = {
              version: 0,
              secret: classroomStudentCodes[code],
            }
            return acc
          }, {}),
          classrooms: {},
        },
      })

      const refreshedStudents = studentsResponse.data.students.items
      setStudents((prev) => {
        const existing = prev || []
        const newStudents = Object.keys(refreshedStudents).map(
          (s) => new ClientStudent(refreshedStudents[s])
        )
        const existingCodes = new Set(existing.map((s) => s.code))
        const uniqueNew = newStudents.filter((s) => !existingCodes.has(s.code))
        return [...existing, ...uniqueNew]
      })

      const classroomStudents = Object.keys(classroomStudentCodes)
        .map((code) => {
          if (refreshedStudents[code]) {
            return new ClientStudent(refreshedStudents[code])
          }
          return students?.find((s) => s.code === code)
        })
        .filter((s): s is ClientStudentInstance => !!s)
      setStudentsPerClassroom((prev) => ({
        ...(prev || {}),
        [classroomCode]: classroomStudents,
      }))
    },
    [students]
  )

  const refreshClassroom = useCallback(
    async (classroomCode: string, secret: string) => {
      const classroomResponse = await api({
        url: '/api2/refresh',
        method: 'POST',
        data: {
          teachers: {},
          classrooms: {
            [classroomCode]: {
              version: 0,
              secret,
            },
          },
          students: {},
        },
      })

      const refreshedClassrooms = classroomResponse.data.classrooms.items
      setClassrooms((prev) => ({
        ...(prev || {}),
        ...refreshedClassrooms,
      }))

      return refreshedClassrooms[classroomCode]
    },
    []
  )

  const ensureStudentsAndClassroom = useCallback(
    async (
      requestedStudentCodes: string[],
      classroomCode: string
    ): Promise<void> => {
      if (!classroomCodes) return

      const isNewClassroom = !classroomCodes[classroomCode]
      let needsClassroomRefresh = !classrooms || !classrooms[classroomCode]

      if (!needsClassroomRefresh && classrooms?.[classroomCode]) {
        const c = new ClientClassroom(classrooms[classroomCode])
        const currentCodes = Object.keys(c.studentCodes() || {})
        if (
          currentCodes.length !== requestedStudentCodes.length ||
          !requestedStudentCodes.every((code) => currentCodes.includes(code))
        ) {
          needsClassroomRefresh = true
        }
      }

      const missingStudentCodes = requestedStudentCodes.filter(
        (code) => !studentCodes || !(code in studentCodes)
      )

      if (isNewClassroom) {
        try {
          const refreshedTeacher = await fetchTeacher()
          if (!refreshedTeacher) return

          setInternalTeacherData(refreshedTeacher)
          const updatedClientTeacher = new ClientTeacher(refreshedTeacher)
          const updatedClassroomCodes =
            updatedClientTeacher.classroomCodes(null)

          if (updatedClassroomCodes?.[classroomCode]) {
            const classroomData = await refreshClassroom(
              classroomCode,
              updatedClassroomCodes[classroomCode]
            )

            if (classroomData) {
              const { classroomStudentCodes, studentsToRefresh } =
                processClassroomData(
                  classroomData,
                  classroomCode,
                  missingStudentCodes
                )

              await refreshAndUpdateStudents(
                studentsToRefresh,
                classroomStudentCodes,
                classroomCode
              )
            }
          }
        } catch (err) {
          console.error(err)
        }
        return
      }

      if (needsClassroomRefresh && classroomCodes[classroomCode]) {
        try {
          const classroomData = await refreshClassroom(
            classroomCode,
            classroomCodes[classroomCode]
          )

          if (classroomData) {
            const { classroomStudentCodes, studentsToRefresh } =
              processClassroomData(
                classroomData,
                classroomCode,
                missingStudentCodes
              )

            await refreshAndUpdateStudents(
              studentsToRefresh,
              classroomStudentCodes,
              classroomCode
            )
          }
        } catch (err) {
          console.error(err)
        }
        return
      }

      if (missingStudentCodes.length > 0 && classroomCodes[classroomCode]) {
        try {
          const classroomData = await refreshClassroom(
            classroomCode,
            classroomCodes[classroomCode]
          )

          if (classroomData) {
            const c: ClientClassroomInstance = new ClientClassroom(
              classroomData
            )
            const classroomStudentCodes = c.studentCodes() || {}
            setStudentCodes((prev) => ({
              ...(prev || {}),
              ...classroomStudentCodes,
            }))

            const studentsToRefresh = missingStudentCodes.filter(
              (code) => classroomStudentCodes[code]
            )

            if (studentsToRefresh.length > 0) {
              await refreshAndUpdateStudents(
                studentsToRefresh,
                classroomStudentCodes,
                classroomCode
              )
            }
          }
        } catch (err) {
          console.error(err)
        }
      }
    },
    [
      classroomCodes,
      classrooms,
      studentCodes,
      students,
      fetchTeacher,
      refreshClassroom,
      processClassroomData,
      refreshAndUpdateStudents,
    ]
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateTeacher = (prop: keyof TeacherInstance, value: any) => {
    setInternalTeacherData((prevData) => {
      if (!prevData) return null
      return { ...prevData, [prop]: value }
    })
  }

  return {
    currentTeacher: new ClientTeacher(internalTeacherData),
    updateTeacher,
    classrooms,
    students,
    studentsPerClassroom,
    refreshStudents,
    ensureStudentsAndClassroom,
  }
}
